import React from "react";
import { ArrowRight, Brain, Cpu, Cloud, Database, Shield, BarChart3, Warehouse, Truck, Camera, Radio, Workflow } from "lucide-react";

const Services = () => {
  const mainServices = [
    {
      icon: <Workflow className="w-8 h-8" />,
      title: "Industry Process",
      description: "Digital transformation and automation solutions",
      features: [
        "Digitalization",
        "Automation using AI", 
        "Smart IoT Devices"
      ],
      color: "blue"
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Smart Solutions for EPC Projects",
      description: "Comprehensive AI-driven project management",
      features: [
        "Industry AI Middle Platform",
        "Project Management Solutions",
        "AI Integrated Systems"
      ],
      color: "orange"
    }
  ];

  const subServices = [
    {
      icon: <Cpu className="w-6 h-6" />,
      title: "Industry AI Middle Platform",
      features: ["Risk Identification", "Behaviour Analysis"]
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Project Management Solutions",
      features: [
        "Smart Database",
        "3D Design Software", 
        "Smart Reporting",
        "Master Data Management",
        "Data Governance",
        "Data Security"
      ]
    },
    {
      icon: <Warehouse className="w-6 h-6" />,
      title: "AI Integrated Smart Warehouse Management"
    },
    {
      icon: <Truck className="w-6 h-6" />,
      title: "Procurement & Logistics"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Smart Camp Management",
      features: [
        "24/7 Camera Surveillance",
        "Vehicle Monitoring System", 
        "Remote Management using AI & IoT Devices"
      ]
    },
    {
      icon: <Radio className="w-6 h-6" />,
      title: "Barcode and RFID Solutions"
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: "bg-blue-50 text-blue-600 border-blue-200",
      orange: "bg-orange-50 text-orange-600 border-orange-200"
    };
    return colors[color] || colors.blue;
  };

  return (
    <section className="bg-[#f2f6fc] py-16 px-6 md:px-12">
      {/* Title */}
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
          <span className="text-orange-500">««</span> Our Services{" "}
          <span className="text-orange-500">»»</span>
        </h2>
        <p className="mt-3 text-gray-700 text-sm md:text-base max-w-3xl mx-auto">
          Delivering cutting-edge digital, AI, and IoT solutions for EPC projects in the Oil & Gas sector
        </p>
      </div>

      {/* Main Services */}
      <div className="grid md:grid-cols-2 gap-8 max-w-7xl mx-auto mb-12">
        {mainServices.map((service, index) => (
          <div
            key={index}
            className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border-l-4 border-orange-500"
          >
            <div className={`inline-flex items-center justify-center p-3 rounded-lg ${getColorClasses(service.color)} mb-4`}>
              {service.icon}
            </div>
            <h3 className="font-bold text-xl text-gray-900 mb-3">
              {service.title}
            </h3>
            <p className="text-gray-600 mb-4">
              {service.description}
            </p>
            <ul className="space-y-2">
              {service.features.map((feature, idx) => (
                <li key={idx} className="flex items-center text-sm text-gray-700">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Detailed Services Grid */}
      <div className="max-w-7xl mx-auto">
        <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
          Detailed Solutions & Services
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {subServices.map((service, index) => (
            <div
              key={index}
              className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-200"
            >
              <div className="flex items-start gap-4">
                <div className="inline-flex items-center justify-center p-2 bg-blue-50 text-blue-600 rounded-lg">
                  {service.icon}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    {service.title}
                  </h4>
                  {service.features && (
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-xs text-gray-600">
                          <ArrowRight className="w-3 h-3 text-orange-500 mr-2" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                  <a
                    href="#"
                    className="inline-flex items-center text-blue-600 font-medium text-sm mt-4 hover:underline"
                  >
                    Learn more <ArrowRight className="w-4 h-4 ml-1" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto mt-12 bg-gradient-to-r from-blue-600 to-orange-500 rounded-2xl p-8 text-center text-white">
        <h3 className="text-2xl font-bold mb-4">
          Ready to Transform Your EPC Projects?
        </h3>
        <p className="mb-6 opacity-90">
          Let us help you implement cutting-edge digital solutions for your next project
        </p>
        <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300 inline-flex items-center">
          Get Started <ArrowRight className="w-5 h-5 ml-2" />
        </button>
      </div>
    </section>
  );
};

export default Services;